import React from 'react';
import { axius, images, mapFx, types } from 'utils';
import { _update } from '_Store';
import { Form, InputGroup } from 'react-bootstrap';
import { Touchable } from '.';
import { useAlert } from 'utils/alert';

interface Props {
	onChange?: (address: types.Address[]) => void;
}
const MapsInputAutocomplete: React.FC<Props> = (props: Props) => {
	const [input, setInput] = React.useState('');
	const [loading, setLoading] = React.useState(false);

	React.useEffect(() => {
		if (input && input.trim().length > 3) {
			const delayDebounceFn = setTimeout(() => {
				getPredictions();
			}, 1000);
			return () => clearTimeout(delayDebounceFn);
		}
		// eslint-disable-next-line
	}, [input]);

	const getPredictions = () => {
		setLoading(true);
		mapFx
			.autocomplete(input)
			.then((res) => {
				props.onChange && props.onChange(res);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	return (
		<React.Fragment>
			<div id="MapsInputAutocomplete">
				<InputGroup className="mb-2">
					<InputGroup.Prepend>
						<InputGroup.Text className="border-right-0 bg-white px-2">
							{loading ? (
								<i className="fa fa-spin fa-circle-notch primary" />
							) : (
								<img
									src={images.Location2}
									alt="Location"
									style={{ height: 17, opacity: 0.5 }}
								/>
							)}
						</InputGroup.Text>
					</InputGroup.Prepend>
					<Form.Control
						className="border-left-0 pl-1"
						type="text"
						autoComplete="off"
						placeholder="Enter new location"
						onChange={(e) => setInput(e.target.value)}
					/>
				</InputGroup>
			</div>
		</React.Fragment>
	);
};

export const Places = ({ addresses }: { addresses: types.Address[] }) => {
	const [presentAlert] = useAlert();

	const updateUserAddress = (address: types.Address, action?: string) => {
		axius.put(`users/me`, { address, action }).then((res) => {
			if (res.status === 200) {
				_update.auth('user', res.data);
			}
		});
	};

	const changeLocationHandler = async (address: types.Address) => {
		let selectedAddress = address;
		if (address.id) {
			const place = await mapFx.placeDetails(address.id);
			if (!place) return;
			selectedAddress = mapFx.isAddressComplete(place)
				? place
				: (await mapFx.promptAddressDetails(presentAlert, place)) || place;
		}
		if (mapFx.isAddressComplete(selectedAddress)) {
			_update.data('location', { address: selectedAddress }, () => {
				updateUserAddress(selectedAddress);
			});
		}
	};

	const deleteAddress = (address: types.Address) => {
		presentAlert({
			header: 'Delete Address',
			message: `Are you sure you want to delete this address?`,
			buttons: [
				'Cancel',
				{
					text: 'Delete',
					role: 'destructive',
					handler: () => {
						updateUserAddress(address, 'rem_address');
					},
				},
			],
		});
	};

	return (
		<React.Fragment>
			<div
				className="no-scroll"
				style={{ maxHeight: '56vh', overflowY: 'scroll' }}
			>
				{addresses.map((address, i) => {
					const isSearch = Boolean(address.id);
					return (
						<div
							key={`address_${i}`}
							className="small"
							style={{ backgroundColor: isSearch ? '#efefef' : '' }}
						>
							<div className="d-flex justify-content-between align-items-center">
								<Touchable
									className="d-flex align-items-start truncate py-2"
									style={{ width: isSearch ? '100%' : '90%' }}
									onClick={() => changeLocationHandler(address)}
								>
									<img
										src={images.Location2}
										className="mr-2 pt-1"
										alt="Location"
										style={{ opacity: 0.5, height: 20 }}
									/>
									<div className="fw-500">{address.name}</div>
								</Touchable>
								{!isSearch && (
									<Touchable
										shape="circle"
										className="hides"
										onClick={() => deleteAddress(address)}
									>
										<i className="fa fa-trash text-danger px-2 py-2" />
									</Touchable>
								)}
							</div>
							{addresses.length - 1 !== i && <hr className="my-0" />}
						</div>
					);
				})}
			</div>
		</React.Fragment>
	);
};

export default MapsInputAutocomplete;
